import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Plus } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AppointmentsPage({ token }) {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [offices, setOffices] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    office_type: '',
    office_name: '',
    date: '',
    time: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, officesRes] = await Promise.all([
        axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAppointments(appointmentsRes.data);
      setOffices(officesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/appointments`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('appointment_success'));
      setDialogOpen(false);
      setFormData({ office_type: '', office_name: '', date: '', time: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to book appointment');
    }
  };

  const officeTypes = [
    { value: 'post_office', label: t('post_office') },
    { value: 'misrad_hapanim', label: t('misrad_hapanim') },
    { value: 'misrad_harishoui', label: t('misrad_harishoui') },
    { value: 'misrad_darkonim', label: t('misrad_darkonim') },
    { value: 'maaser_hnassa', label: t('maaser_hnassa') },
    { value: 'bituah_leumi', label: t('bituah_leumi') },
    { value: 'municipality', label: t('municipality') }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight" data-testid="appointments-title">
            {t('appointments')}
          </h1>
          <p className="text-lg text-slate-600 mt-2">{t('my_appointments')}</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-12 px-6 gradient-blue shadow-lg shadow-blue-600/30" data-testid="book-appointment-button">
              <Plus className="h-5 w-5 mr-2" />
              {t('book_appointment')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="appointment-dialog">
            <DialogHeader>
              <DialogTitle>{t('book_appointment')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label>{t('office_type')}</Label>
                <Select
                  value={formData.office_type}
                  onValueChange={(value) => setFormData({ ...formData, office_type: value, office_name: '' })}
                >
                  <SelectTrigger className="mt-2" data-testid="select-office-type">
                    <SelectValue placeholder={t('office_type')} />
                  </SelectTrigger>
                  <SelectContent>
                    {officeTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.office_type && (
                <div>
                  <Label>{t('office_name')}</Label>
                  <Select value={formData.office_name} onValueChange={(value) => setFormData({ ...formData, office_name: value })}>
                    <SelectTrigger className="mt-2" data-testid="select-office-name">
                      <SelectValue placeholder={t('office_name')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(offices[formData.office_type] || []).map((office) => (
                        <SelectItem key={office} value={office}>
                          {office}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>{t('select_date')}</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-2"
                  min={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="input-date"
                />
              </div>

              <div>
                <Label>{t('select_time')}</Label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="mt-2"
                  required
                  data-testid="input-time"
                />
              </div>

              <Button type="submit" className="w-full rounded-full gradient-blue" data-testid="confirm-booking-button">
                {t('confirm_booking')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Appointments List */}
      {appointments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="appointments-grid">
          {appointments.map((appointment) => (
            <Card
              key={appointment.id}
              className="p-6 bg-white rounded-2xl border border-slate-200 hover:shadow-lg transition-all duration-300"
              data-testid="appointment-card"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <h3 className="text-lg font-bold text-slate-900">{appointment.office_name}</h3>
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                    {t(appointment.status)}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm">{t(appointment.office_type)}</span>
                </div>

                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm">{new Date(appointment.date).toLocaleDateString()}</span>
                </div>

                <div className="flex items-center gap-2 text-slate-600">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">{appointment.time}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 bg-white rounded-2xl border border-slate-200 text-center" data-testid="no-appointments">
          <Calendar className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <p className="text-xl text-slate-600">{t('no_appointments')}</p>
        </Card>
      )}
    </div>
  );
}